import * as React from "react"

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"

import { Badge } from "../ui/badge"
import Fuse from "fuse.js"
import { Link } from "@tanstack/react-router"

import type { BlogPost } from "@/lib/blog"
import { Button } from "../ui/button"

type Props = {
  posts: BlogPost[];
}

export const SearchBar = ({ posts }: Props) => {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState("")

  const fuse = React.useMemo(() => {
    const fuseOptions = {
      isCaseSensitive: false,
      threshold: 0.4,
      ignoreLocation: true,
      keys: [
        { name: "title", weight: 2 },
        { name: "description", weight: 1.5 },
        { name: "tags", weight: 1 }
      ]
    };
    return new Fuse(posts, fuseOptions);
  }, [posts]);

  const filteredPosts = React.useMemo(() => {
    if (!query || query.length < 2) return posts;
    return fuse.search(query).map((r) => r.item);
  }, [fuse, query, posts]);

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((open) => !open)
      }
    }

    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [])


  return (
    <div>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="default" className="px-2 py-0" onClick={() => setOpen(_value => true)}>
            <p className="text-sm text-muted-foreground flex gap-1 items-center flex-row justify-center gap-x-1">
              📝 Search{" "}
            </p>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <kbd>
            <p className="text-xs">
              Press{" "}
              <span className="text-xs">⌘</span>K
            </p>
          </kbd>
        </TooltipContent>
      </Tooltip>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          value={query}
          onValueChange={setQuery}
          placeholder="Search for keywords..."
        />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Suggestions">
            {filteredPosts.map((post) => (
              <CommandItem key={post.slug} >
                <Link to={`/post/$slug`}
                  params={{ slug: post.slug }}
                  onClick={() => {
                    setOpen(false)
                    setQuery("")
                  }}
                  className="w-full h-full flex flex-col gap-y-2 items-start"
                >
                  <p className='text-lg font-bold'>{`${post.lang === 'fr' ? '🇫🇷' : '🇬🇧'} - ${post.title}`}</p>
                  <div className="flex gap-x-2">
                    {post.tags.map(tag => (
                      <Badge key={tag} variant="outline">{tag}</Badge>
                    ))}
                  </div>
                  <p className='text-sm opacity-80'>{post.description}</p>
                </Link>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </div>
  )
}
